import type { ResultOf, VariablesOf } from "gql.tada";
import {
  BroadcastClipProgramFragment,
  BroadcastClipStationFragment,
} from "@/data-access/discovery-view/fragments/discovery-view-fragments";
import { graphql } from "@/data-access/graphql";

// ============================================================================
// Broadcast Clips
// ============================================================================

/**
 * Sort ordering for broadcast clips queries.
 */
type OrderingValue =
  | "ASC"
  | "DESC"
  | "ASC_NULLS_FIRST"
  | "ASC_NULLS_LAST"
  | "DESC_NULLS_FIRST"
  | "DESC_NULLS_LAST";

export type BroadcastClipOrdering = {
  airDatetimeUtc?: OrderingValue;
  localAdValueCents?: OrderingValue;
  localAudience?: OrderingValue;
  nationalAdValueCents?: OrderingValue;
  nationalAudience?: OrderingValue;
  title?: OrderingValue;
  stationName?: OrderingValue;
};

export const GetBroadcastClips = graphql(
  `
    query getBroadcastClips(
      $filters: BroadcastClipFilter
      $ordering: [BroadcastClipOrder!]
      $pagination: OffsetPaginationInput
    ) {
      broadcastClips(
        filters: $filters
        ordering: $ordering
        pagination: $pagination
      ) {
        totalCount
        results {
          id
          title
          transcriptExcerpt
          airDatetimeUtc
          durationFormatted
          totalAudience
          totalAdValue
          nationalAudience
          nationalAdValueCents
          nationalAdValue
          localAdRate
          localAdValue
          localAdValueCents
          localAudience
          station {
            ...BroadcastClipStationFragment
          }
          program {
            ...BroadcastClipProgramFragment
          }
        }
      }
    }
  `,
  [BroadcastClipStationFragment, BroadcastClipProgramFragment],
);

/** Broadcast clip type inferred from the GraphQL query via gql.tada. */
export type BroadcastClip = ResultOf<
  typeof GetBroadcastClips
>["broadcastClips"]["results"][number];

/** Filter options for fetching broadcast clips. */
export interface FetchBroadcastClipsFilters {
  feedId?: string;
  airDateStart?: string;
  airDateEnd?: string;
  searchMatch?: string;
  keyMessageIds?: string[];
}

// ============================================================================
// Articles
// ============================================================================

import {
  NarrativeClusterStatsFragment,
  NarrativeClustersTableRowFragment,
} from "@/data-access/discovery-view/fragments/discovery-view-fragments";
import {
  FeedArticleProjectionFragment,
  OffsetPaginationInfoFragment,
} from "@/data-access/shared/fragments/feed-article-projection-fragments";
export const GetFeedArticlesTableData = graphql(
  `
    query getFeedArticlesTableData(
      $ordering: [FeedArticleProjectionOrder!]!
      $filters: FeedArticleProjectionFilter!
      $pagination: OffsetPaginationInput
    ) {
      feedArticleProjectionsTable(
        ordering: $ordering
        filters: $filters
        pagination: $pagination
      ) {
        pageInfo {
          ...OffsetPaginationInfoFragment
        }
        totalCount
        results {
          ...FeedArticleProjectionFragment
        }
      }
    }
  `,
  [FeedArticleProjectionFragment, OffsetPaginationInfoFragment],
);

export type GetFeedArticlesTableDataResult = ResultOf<
  typeof GetFeedArticlesTableData
>;

export type GetFeedArticlesTableDataVariables = VariablesOf<
  typeof GetFeedArticlesTableData
>;

/**
 * Article type from query results
 */
export type GetFeedArticlesTableDataArticle =
  GetFeedArticlesTableDataResult["feedArticleProjectionsTable"]["results"][number];

export const GetNarrativeClustersTable = graphql(
  `
    query getNarrativeClustersTable(
      $projectionFilters: FeedArticleProjectionFilter!
      $filters: StoryFilter
      $pagination: OffsetPaginationInput
      $ordering: [StoryOrder!]
    ) {
      narrativeClusterTable: storiesTable(
        projectionFilters: $projectionFilters
        filters: $filters
        pagination: $pagination
        ordering: $ordering
      ) {
        pageInfo {
          ...OffsetPaginationInfoFragment
        }
        results {
          ...NarrativeClustersTableRowFragment
        }
        totalCount
      }
    }
  `,
  [
    OffsetPaginationInfoFragment,
    NarrativeClustersTableRowFragment,
    NarrativeClusterStatsFragment,
  ],
);

export type GetNarrativeClustersTableResult = ResultOf<
  typeof GetNarrativeClustersTable
>;

export type GetNarrativeClustersTableVariables = VariablesOf<
  typeof GetNarrativeClustersTable
>;

/** One row from GetNarrativeClustersTable query results */
export type GetNarrativeClustersTableRow =
  GetNarrativeClustersTableResult["narrativeClusterTable"]["results"][number];

// ============================================================================
// Podcast Episodes
// ============================================================================

export const GetPodcastEpisodes = graphql(`
  query getPodcastEpisodes(
    $filters: PodcastEpisodeFilter!
    $ordering: [PodcastEpisodeOrder!]
    $pagination: OffsetPaginationInput
  ) {
    podcastEpisodes(
      filters: $filters
      ordering: $ordering
      pagination: $pagination
    ) {
      totalCount
      results {
        id
        title
        subtitle
        description
        author
        sourceTitle
        sourceCountry
        language
        publishDate
        durationFormatted
        archiveUrl
        imageUrl
        status
        estimatedAudience
        transcriptExcerpt
      }
    }
  }
`);

/** Podcast episode type inferred from the GraphQL query via gql.tada. */
export type PodcastEpisode = ResultOf<
  typeof GetPodcastEpisodes
>["podcastEpisodes"]["results"][number];

/**
 * Sort ordering for podcast episodes queries.
 * Mirrors the PodcastEpisodeOrder GraphQL input type.
 */
export type PodcastEpisodeOrdering = {
  title?: OrderingValue;
  publishDate?: OrderingValue;
  sourceTitle?: OrderingValue;
  author?: OrderingValue;
  language?: OrderingValue;
  sourceCountry?: OrderingValue;
  durationMs?: OrderingValue;
  estimatedAudience?: OrderingValue;
};

/** Filter options for fetching podcast episodes. */
export interface FetchPodcastEpisodesFilters {
  feedId?: string;
  publishDateAfter?: string;
  publishDateBefore?: string;
  sourceTitle?: string;
  language?: string;
  sourceCountry?: string;
  searchMatch?: string;
  keyMessageIds?: string[];
}

// ============================================================================
// Exclusion Mutations
// ============================================================================

export const ExcludePodcastEpisodes = graphql(`
  mutation excludePodcastEpisodes(
    $episodeIds: [ID!]!
    $feedId: ID!
    $reason: String!
  ) {
    excludePodcastEpisodes(
      episodeIds: $episodeIds
      feedId: $feedId
      reason: $reason
    ) {
      ... on SuccessResponse {
        __typename
        message
      }
      ... on OperationInfo {
        __typename
        messages {
          kind
          message
          field
          code
        }
      }
    }
  }
`);

export const ExcludeBroadcastClips = graphql(`
  mutation excludeBroadcastClips(
    $clipIds: [ID!]!
    $feedId: ID!
    $reason: String!
  ) {
    excludeBroadcastClips(clipIds: $clipIds, feedId: $feedId, reason: $reason) {
      ... on SuccessResponse {
        __typename
        message
      }
      ... on OperationInfo {
        __typename
        messages {
          kind
          message
          field
          code
        }
      }
    }
  }
`);

// ============================================================================
// TVEyes MediaViewport — on-demand player URL queries
// Fetched lazily when the user clicks the play/headphones button.
// requestPolicy: "network-only" is set in TVEyesPlayer.tsx to ensure a fresh
// signed URL on every open (signed URLs expire after ~9.5 minutes).
// ============================================================================

export const GetBroadcastClipPlayerUrl = graphql(`
  query getBroadcastClipPlayerUrl($id: ID!, $feedId: ID!) {
    broadcastClips(filters: { id: { exact: $id }, feedId: $feedId }) {
      results {
        playerUrl(slim: false, enableArchive: true)
      }
    }
  }
`);

export const GetPodcastEpisodePlayerUrl = graphql(`
  query getPodcastEpisodePlayerUrl($id: ID!, $feedId: ID!) {
    podcastEpisodes(filters: { id: { exact: $id }, feedId: $feedId }) {
      results {
        playerUrl(slim: false, enableArchive: true)
      }
    }
  }
`);

// TVEyes archive undo mutations — deleting the SavedClip flips ``playerUrl``
// from the saved-media URL back to the live editor URL. Idempotent on the
// server (0-row delete still returns SuccessResponse).

export const UnsaveBroadcastClip = graphql(`
  mutation unsaveBroadcastClip($feedId: ID!, $broadcastClipId: ID!) {
    unsaveBroadcastClip(feedId: $feedId, broadcastClipId: $broadcastClipId) {
      ... on SuccessResponse {
        __typename
        message
      }
      ... on OperationInfo {
        __typename
        messages {
          message
        }
      }
    }
  }
`);

export const UnsavePodcastEpisode = graphql(`
  mutation unsavePodcastEpisode($feedId: ID!, $podcastEpisodeId: ID!) {
    unsavePodcastEpisode(feedId: $feedId, podcastEpisodeId: $podcastEpisodeId) {
      ... on SuccessResponse {
        __typename
        message
      }
      ... on OperationInfo {
        __typename
        messages {
          message
        }
      }
    }
  }
`);
